import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import { Shield, Search, PlusCircle, Edit3, Trash2, ShieldAlert } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { GetSubUsers, AddSubUser, UpdateSubUser, DeleteSubUser, GetSqlUsers, clearMessages } from '../../Redux/Slice/UserRight/UserRightSlice';
import { toast, ToastContainer } from 'react-toastify';
import Cookies from 'js-cookie';
import { Fingerprint } from 'lucide-react';
import { baseMenuData } from "../../Layout/Sidebar/menuConfig";
import { RegisterFingerprint, GetMyDevices, DeleteDevice } from '../../Redux/Slice/WebAuthn/WebAuthnSlice';

export const UserRight = () => {
    const dispatch = useDispatch();
    const isdark = Cookies.get("theme") || "light";
    const activeDatabase = Cookies.get("Database");

    const { subUsers, sqlUsers, sqlUsersData1, sqlUsersData2, loading, error, successMessage } = useSelector((state) => state.UserRightSlice);
    const { FilterProjectdata } = useSelector((state) => state.ClientLogin);
    const { devices: fingerprintDevices, registerLoading: fpLoading } = useSelector((state) => state.WebAuthn);

    // List of available projects
    const projects = FilterProjectdata?.Project_list || [];

    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showFingerprintPanel, setShowFingerprintPanel] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
    const [editingUserId, setEditingUserId] = useState(null);

    const [formData, setFormData] = useState({
        Name: '',
        UserName: '',
        Password: '',
        MobileNo: '',
        EmailID: '',
        Role: 'User', // 'User' | 'Admin'
        IsActive: 'Yes',
        ProjectID: [], // Array of string IDs
        AllowedPages: [], // Array of page paths
        SqlId: '',
        SqlName: ''
    });

    useEffect(() => {
        dispatch(GetSubUsers());
        dispatch(GetMyDevices());
        if (activeDatabase) {
            dispatch(GetSqlUsers(activeDatabase));
        }
    }, [dispatch, activeDatabase]);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(clearMessages());
        }
        if (error) {
            const errorMsg = typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error);
            toast.error(errorMsg);
            dispatch(clearMessages());
        }
    }, [successMessage, error, dispatch]);

    const handleOpenAdd = () => {
        setFormData({
            Name: '',
            UserName: '',
            Password: '',
            MobileNo: '',
            EmailID: '',
            Role: 'User',
            IsActive: 'Yes',
            ProjectID: [],
            AllowedPages: [],
            SqlId: '',
            SqlName: ''
        });
        setModalMode('add');
        setShowModal(true);
    };

    const handleOpenEdit = (user) => {
        setFormData({
            Name: user.ClientName || user.OwnerName || user.Name || '',
            UserName: user.User_ID || user.UserName || '',
            Password: user.Login_password || user.Password || '',
            MobileNo: user.MobileNo || '',
            EmailID: user.EmailID || '',
            Role: user.Role || 'User',
            IsActive: (user.IsActive === true || user.IsActive === 'Yes') ? 'Yes' : 'No',
            ProjectID: user.ProjectID ? user.ProjectID.map(p => p.ID?._id || p.ID).filter(Boolean) : [],
            AllowedPages: user.AllowedPages || [],
            SqlId: user.SqlId || '',
            SqlName: user.SqlName || ''
        });
        setEditingUserId(user._id);
        setModalMode('edit');
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this sub-user?")) {
            dispatch(DeleteSubUser(id));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTogglePage = (pagePath) => {
        setFormData(prev => {
            const isSelected = prev.AllowedPages.includes(pagePath);
            const updated = isSelected
                ? prev.AllowedPages.filter(p => p !== pagePath)
                : [...prev.AllowedPages, pagePath];
            return { ...prev, AllowedPages: updated };
        });
    };

    const handleToggleProject = (projectId) => {
        setFormData(prev => {
            const isSelected = prev.ProjectID.includes(projectId);
            const updated = isSelected
                ? prev.ProjectID.filter(id => id !== projectId)
                : [...prev.ProjectID, projectId];
            return { ...prev, ProjectID: updated };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.Name.trim() || !formData.UserName.trim() || !formData.Password.trim()) {
            toast.error("Name, Username, and Password are required fields");
            return;
        }

        // Format payloads for MongoDB Schema
        const payload = {
            ...formData,
            ProjectID: formData.ProjectID.map(id => ({ ID: id }))
        };

        try {
            if (modalMode === 'add') {
                await dispatch(AddSubUser(payload)).unwrap();
            } else {
                await dispatch(UpdateSubUser({ id: editingUserId, data: payload })).unwrap();
            }
            setShowModal(false);
        } catch (err) {
            // Catch error to prevent unhandled promise rejection crash.
            // The validation error is already beautifully displayed as a Toast by the useEffect redux listener.
            console.error("Sub-user save failed:", err);
        }
    };

    // Filter sub-users based on search query
    const filteredUsers = subUsers.filter(user =>
        (user.ClientName || user.Name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.User_ID || user.UserName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.EmailID || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const styles = {
        main: {
            background: isdark === 'light' ? '#f4f7fa' : '#020617',
            minHeight: '100vh',
            padding: '24px',
            fontFamily: "'Inter', sans-serif",
            color: isdark === 'light' ? '#0f172a' : '#f8fafc'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
        },
        card: {
            background: isdark === 'light' ? '#ffffff' : '#0f172a',
            border: `1px solid ${isdark === 'light' ? '#e2e8f0' : '#1e293b'}`,
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '20px',
            overflow: 'hidden'
        },
        searchBox: {
            borderRadius: '10px',
            border: `1px solid ${isdark === 'light' ? '#cbd5e1' : '#334155'}`,
            background: isdark === 'light' ? '#ffffff' : '#1e293b',
            color: isdark === 'light' ? '#0f172a' : '#f8fafc',
            paddingLeft: '36px',
            height: '42px'
        },
        badge: {
            borderRadius: '20px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '600'
        },
        permissionSection: {
            border: `1px solid ${isdark === 'light' ? '#cbd5e1' : '#334155'}`,
            borderRadius: '8px',
            padding: '12px',
            maxHeight: '200px',
            overflowY: 'auto',
            background: isdark === 'light' ? '#f8fafc' : '#1e293b'
        }
    };

    return (
        <div style={styles.main}>
            {/* Header section */}
            <div style={styles.header}>
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Shield className="text-primary" size={24} />
                        USER RIGHT MANAGEMENT
                    </h4>
                    <p className="text-muted small mb-0">Create sub-users and configure their specific access privileges across available projects</p>
                </div>
                <Button variant="primary" className="fw-bold px-4 d-flex align-items-center gap-2 shadow" style={{ borderRadius: '10px' }} onClick={handleOpenAdd}>
                    <PlusCircle size={18} /> CREATE USER
                </Button>
            </div>

            {/* Main table container */}
            <Card style={styles.card}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{ position: 'relative', width: '320px' }}>
                        <Search size={18} className="text-muted" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                        <Form.Control
                            type="text"
                            placeholder="Search by name or username..."
                            style={styles.searchBox}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <Badge bg="primary" style={{ padding: '6px 12px', borderRadius: '12px' }}>Total Users: {subUsers.length}</Badge>
                </div>

                <div className="table-responsive">
                    <Table hover bordered className="mb-0 text-nowrap align-middle" style={{ fontSize: '13.5px' }}>
                        <thead className={isdark === 'light' ? 'bg-light' : 'bg-dark'}>
                            <tr>
                                <th>Name</th>
                                <th>Username</th>
                                <th>Role</th>
                                <th>Bound SQL User</th>
                                <th>Email / Contact</th>
                                <th>Status</th>
                                <th>Allowed Projects</th>
                                <th className="text-center" style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && subUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4">
                                        <div className="spinner-border text-primary spinner-border-sm me-2" />
                                        Loading users list...
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4 text-muted">
                                        No users found. Click 'Create User' to add a new sub-user.
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user._id}>
                                        <td className="fw-bold">{user.ClientName || user.Name}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{user.User_ID || user.UserName}</span>
                                        </td>
                                        <td>
                                            <span className={`badge ${user.Role === 'Admin' ? 'bg-primary-subtle text-primary border border-primary-subtle' : 'bg-secondary-subtle text-secondary border border-secondary-subtle'}`} style={styles.badge}>
                                                {user.Role || 'User'}
                                            </span>
                                        </td>
                                        <td>
                                            {user.SqlName ? (
                                                <Badge bg="success" style={{ fontSize: '11px' }}>
                                                    {user.SqlName} ({user.SqlId})
                                                </Badge>
                                            ) : (
                                                <span className="text-muted small">None</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="small text-muted">{user.EmailID || 'No Email'}</div>
                                            <div className="small text-muted">{user.MobileNo || 'No Mobile'}</div>
                                        </td>
                                        <td>
                                            <span className={`badge ${(user.IsActive === true || user.IsActive === 'Yes') ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`} style={styles.badge}>
                                                {(user.IsActive === true || user.IsActive === 'Yes') ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '250px' }}>
                                                {user.ProjectID && user.ProjectID.length > 0 ? (
                                                    user.ProjectID.map((p, idx) => (
                                                        <Badge key={idx} bg="primary" style={{ fontSize: '10px' }}>
                                                            {p.ID?.ProjectName || 'Project'}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-muted small">None</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Button size="sm" variant="outline-primary" style={{ borderRadius: '6px' }} onClick={() => handleOpenEdit(user)}>
                                                    <Edit3 size={14} />
                                                </Button>
                                                <Button size="sm" variant="outline-danger" style={{ borderRadius: '6px' }} onClick={() => handleDelete(user._id)}>
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* ================= FINGERPRINT CONFIGURATION ================= */}
            <Card style={{ ...styles.card, marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showFingerprintPanel ? '16px' : '0' }}>
                    <h5 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: isdark === 'light' ? '#0f172a' : '#f8fafc' }}>
                        <Fingerprint size={20} className="text-primary" />
                        Fingerprint Authentication
                    </h5>
                    <Button 
                        variant={showFingerprintPanel ? "secondary" : "primary"} 
                        size="sm" 
                        style={{ borderRadius: '8px' }}
                        onClick={() => setShowFingerprintPanel(!showFingerprintPanel)}
                    >
                        {showFingerprintPanel ? 'Hide Details' : 'Manage Devices'}
                    </Button>
                </div>

                {showFingerprintPanel && (
                    <Row>
                        <Col md={8} lg={6}>
                            <div style={{ 
                                background: isdark === 'light' ? '#f8fafc' : '#1e293b', 
                                border: `1px solid ${isdark === 'light' ? '#e2e8f0' : '#334155'}`,
                                borderRadius: '12px', 
                                padding: '16px' 
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="fw-semibold small">Registered Devices ({fingerprintDevices.length})</span>
                                    <Button 
                                        variant="success" 
                                        size="sm" 
                                        disabled={fpLoading}
                                        style={{ borderRadius: '8px', fontSize: '12px' }}
                                        onClick={async () => {
                                            const result = await dispatch(RegisterFingerprint("My Device"));
                                            if (result.meta.requestStatus === "fulfilled") {
                                                toast.success("✅ Fingerprint registered!");
                                                dispatch(GetMyDevices());
                                            } else {
                                                toast.error(result.payload || "Fingerprint registration failed");
                                            }
                                        }}
                                    >
                                        {fpLoading ? 'Scanning...' : '+ Register New Device'}
                                    </Button>
                                </div>

                                {fingerprintDevices.length === 0 ? (
                                    <div className="text-muted small text-center py-3">No fingerprints registered yet.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {fingerprintDevices.map((device, i) => (
                                            <div key={i} style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                padding: '10px 14px',
                                                background: isdark === 'light' ? '#ffffff' : '#0f172a',
                                                border: `1px solid ${isdark === 'light' ? '#e2e8f0' : '#334155'}`,
                                                borderRadius: '8px'
                                            }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '16px' }}>📱</span>
                                                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{device.deviceName}</span>
                                                </div>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '6px' }}
                                                    onClick={() => {
                                                        if(window.confirm("Remove this fingerprint device?")) {
                                                            dispatch(DeleteDevice(device.credentialId));
                                                        }
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
            </Card>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className={isdark === 'light' ? '' : 'modal-dark'}>
                <Modal.Header closeButton className={isdark === 'light' ? 'bg-light' : 'bg-dark text-white border-secondary'}>
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        {modalMode === 'add' ? <PlusCircle size={22} className="text-primary" /> : <Edit3 size={22} className="text-primary" />}
                        {modalMode === 'add' ? 'CREATE SUB-USER' : 'EDIT USER RIGHTS'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={isdark === 'light' ? '' : 'bg-dark text-white border-secondary'}>
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Full Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="Name"
                                        placeholder="Enter full name"
                                        required
                                        value={formData.Name}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Username (Login ID) *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="UserName"
                                        placeholder="Enter unique username"
                                        required
                                        value={formData.UserName}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Password *</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="Password"
                                        placeholder="Enter login password"
                                        required
                                        value={formData.Password}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Status</Form.Label>
                                    <Form.Select
                                        name="IsActive"
                                        value={formData.IsActive}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    >
                                        <option value="Yes">Active</option>
                                        <option value="No">Inactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Role *</Form.Label>
                                    <Form.Select
                                        name="Role"
                                        value={formData.Role}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setFormData(prev => ({
                                                ...prev,
                                                Role: value,
                                                SqlId: '', // Reset SQL selection on role change to ensure valid pairing
                                                SqlName: '',
                                                ProjectID: value === 'Collection Man' ? [] : prev.ProjectID
                                            }));
                                        }}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    >
                                        <option value="User">User</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Collection Man">Collection Man</option>
                                        <option value="millstageuser">millstageuser</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Bind SQL Server User</Form.Label>
                                    <Form.Select
                                        name="SqlUser"
                                        value={formData.SqlId ? `${formData.SqlId}|${formData.SqlName}` : ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val) {
                                                const [id, name] = val.split('|');
                                                setFormData(prev => ({
                                                    ...prev,
                                                    SqlId: id,
                                                    SqlName: name
                                                }));
                                            } else {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    SqlId: '',
                                                    SqlName: ''
                                                }));
                                            }
                                        }}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    >
                                        <option value="">-- Select SQL User --</option>
                                        {formData.Role === 'Collection Man' ? (
                                            (sqlUsersData1 || []).map((u, idx) => (
                                                <option key={idx} value={`${u.CollectionMenID}|${u.CollectionMen}`}>
                                                    {u.CollectionMen} ({u.CollectionMenID})
                                                </option>
                                            ))
                                        ) : formData.Role === 'millstageuser' ? (
                                            (sqlUsersData2 || []).map((u, idx) => (
                                                <option key={idx} value={`${u.userID}|${u.userName}`}>
                                                    {u.userName} ({u.userID})
                                                </option>
                                            ))
                                        ) : (
                                            (sqlUsers || []).map((u, idx) => (
                                                <option key={idx} value={`${u.userID}|${u.userName}`}>
                                                    {u.userName} ({u.userID})
                                                </option>
                                            ))
                                        )}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Email ID</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="EmailID"
                                        placeholder="Enter email address"
                                        value={formData.EmailID}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold mb-1">Mobile No</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="MobileNo"
                                        placeholder="Enter mobile number"
                                        value={formData.MobileNo}
                                        onChange={handleInputChange}
                                        style={{ background: isdark === 'light' ? '#ffffff' : '#1e293b', color: 'inherit' }}
                                    />
                                </Form.Group>
                            </Col>

                            {formData.Role !== 'Collection Man' && (
                                <>
                                    <hr className="my-4 border-secondary" />

                                    <h5 className="fw-bold mb-2 text-primary d-flex align-items-center gap-1">
                                        <ShieldAlert size={18} /> CONFIGURE PERMISSIONS
                                    </h5>

                                    {/* PROJECTS PERMISSION */}
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold mb-1 d-flex justify-content-between">
                                            <span>ALLOWED PROJECTS</span>
                                            <Badge bg="primary">{formData.ProjectID.length} / {projects.length}</Badge>
                                        </Form.Label>
                                        <div style={styles.permissionSection}>
                                            {projects.length === 0 ? (
                                                <div className="text-muted small">No active projects loaded</div>
                                            ) : (
                                                <Row>
                                                    {projects.map((proj) => {
                                                        const pId = proj.ID || proj.DashboardProjectID;
                                                        const isSelected = formData.ProjectID.includes(pId);
                                                        
                                                        const projectMapping = {
                                                          "MILL STAGE": "MILLSTAGE",
                                                          MILLSTAGE: "MILLSTAGE",
                                                          STORE: "MAINTENANCE",
                                                          MAINTENANCE: "MAINTENANCE",
                                                          GREY: "GREY",
                                                          FAS: "FAS",
                                                          PAYROLL: "PAYROLL",
                                                          COLOUR: "COLOUR",
                                                          "ORDER APPROVAL": "ORDER APPROVAL",
                                                          "OVERALL DASHBOARD": "OVERALL DASHBOARD",
                                                          ORDERAPPROVAL: "ORDER APPROVAL",
                                                          "COLLECTION MAN": "COLLECTION MAN",
                                                            "MILLSTAGE ENTRY": "MILLSTAGE ENTRY",
                                                        };
                                                        
                                                        const mappedName = projectMapping[proj.DashboardProjectName?.toUpperCase()] || proj.DashboardProjectName?.toUpperCase();
                                                        const menuProject = baseMenuData.find(m => m.name === mappedName);
                                                        const pages = menuProject?.submenu || [];

                                                        return (
                                                            <Col md={12} key={pId} className="mb-2 p-2 border rounded" style={{ background: isdark === 'light' ? '#fff' : '#0f172a' }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    id={`proj-${pId}`}
                                                                    label={proj.DashboardProjectName}
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleProject(pId)}
                                                                    className="fw-bold text-primary"
                                                                />
                                                                {isSelected && pages.length > 0 && (
                                                                    <div className="d-flex flex-wrap gap-3 mt-2 ms-4">
                                                                        {pages.map(page => (
                                                                            <Form.Check
                                                                                key={page.path}
                                                                                type="checkbox"
                                                                                id={`page-${pId}-${page.path}`}
                                                                                label={page.name}
                                                                                checked={formData.AllowedPages.includes(page.path)}
                                                                                onChange={() => handleTogglePage(page.path)}
                                                                                className="small text-muted"
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            )}
                                        </div>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <div className="d-flex justify-content-end gap-3 mt-4 border-top pt-3 border-secondary">
                            <Button variant="secondary" style={{ borderRadius: '8px' }} onClick={() => setShowModal(false)}>
                                CANCEL
                            </Button>
                            <Button variant="success" type="submit" className="fw-bold px-4" style={{ borderRadius: '8px' }}>
                                SAVE CHANGES
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
        </div>
    );
};

export default UserRight;
